export const ONBOARDING_PATH = '/configuracao-inicial'

export const ONBOARDING_STEPS = ['site', 'domain', 'brand', 'watermark'] as const

export type OnboardingStep = (typeof ONBOARDING_STEPS)[number]

export type OnboardingStatus = 'pending' | 'in-progress' | 'completed' | 'dismissed'

export interface OnboardingState {
  status: OnboardingStatus
  completedSteps?: OnboardingStep[]
  startedAt?: string | null
  finishedAt?: string | null
}

export const isOnboardingOpen = (state?: OnboardingState | null): state is OnboardingState =>
  state?.status === 'pending' || state?.status === 'in-progress'

export function nextOnboardingStep(completed: OnboardingStep[] = []): OnboardingStep | null {
  return ONBOARDING_STEPS.find(step => !completed.includes(step)) ?? null
}

export function withCompletedStep(completed: OnboardingStep[] = [], step: OnboardingStep): OnboardingStep[] {
  return ONBOARDING_STEPS.filter(item => item === step || completed.includes(item))
}

export function completedOnboardingSteps(state?: OnboardingState | null): OnboardingStep[] {
  return ONBOARDING_STEPS.filter(step => state?.completedSteps?.includes(step))
}

export function firstName(name?: string | null): string {
  return (name ?? '').trim().split(/\s+/)[0] ?? ''
}

export function elapsedLabel(startedAt?: string | null, now: Date = new Date()): string | null {
  if (!startedAt) return null
  const start = new Date(startedAt).getTime()
  if (Number.isNaN(start)) return null
  const seconds = Math.max(1, Math.round((now.getTime() - start) / 1000))
  if (seconds > 60 * 60) return null
  const minutes = Math.floor(seconds / 60)
  const rest = seconds % 60
  if (minutes === 0) return `${rest}s`
  return rest ? `${minutes}min ${rest}s` : `${minutes}min`
}

const MAX_SUBDOMAIN = 40
const MIN_SUBDOMAIN = 3
const SUBDOMAIN = /^(?!-)[a-z0-9-]+(?<!-)$/
const IGNORED_WORDS = new Set(['ltda', 'me', 'mei', 'eireli', 'epp', 'sa', 'cia', 'e', 'de', 'da', 'do', 'das', 'dos'])
const TRADE_WORDS = new Set(['imobiliaria', 'imoveis', 'imobiliarios', 'corretora', 'corretor', 'negocios', 'consultoria'])

export function slugify(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9]+/g, '-')
    .replace(/^-+|-+$/g, '')
}

export function sanitizeSubdomainInput(value: string): string {
  return value
    .normalize('NFD')
    .replace(/[̀-ͯ]/g, '')
    .toLowerCase()
    .replace(/[^a-z0-9-]+/g, '-')
    .replace(/-{2,}/g, '-')
    .replace(/^-+/, '')
    .slice(0, MAX_SUBDOMAIN)
}

export function isValidSubdomain(value: string): boolean {
  return value.length >= MIN_SUBDOMAIN && value.length <= MAX_SUBDOMAIN && SUBDOMAIN.test(value)
}

function fitLabel(words: string[]): string {
  const kept: string[] = []
  for (const word of words) {
    if ([...kept, word].join('-').length > MAX_SUBDOMAIN) break
    kept.push(word)
  }
  return kept.join('-')
}

export function subdomainCandidates(companyName: string): string[] {
  const words = slugify(companyName)
    .split('-')
    .filter(word => word && !IGNORED_WORDS.has(word))
  const full = fitLabel(words)
  const distinctive = fitLabel(words.filter(word => !TRADE_WORDS.has(word)))
  const base = full || 'minha-imobiliaria'

  const preferred = [distinctive.length >= 4 ? distinctive : '', base, distinctive ? `${distinctive}-imoveis` : '']
  const numbered = [2, 3, 4, 5].map(n => `${base.slice(0, MAX_SUBDOMAIN - 2)}-${n}`)

  return Array.from(new Set([...preferred, ...numbered])).filter(isValidSubdomain)
}

export type WatermarkPlacement = 'corner' | 'center' | 'mosaic'

export interface WatermarkDraft {
  enabled: boolean
  type: 'image' | 'text'
  text: string
  placement: WatermarkPlacement
}

interface PlacementPreset {
  position: 'bottom-right' | 'middle-center' | 'random'
  randomCount?: number
  rotation: number
  text: { size: number; opacity: number }
  image: { size: number; opacity: number }
}

const PLACEMENT_PRESETS: Record<WatermarkPlacement, PlacementPreset> = {
  corner: { position: 'bottom-right', rotation: 0, text: { size: 6, opacity: 0.9 }, image: { size: 12, opacity: 0.9 } },
  center: { position: 'middle-center', rotation: 0, text: { size: 9, opacity: 0.6 }, image: { size: 24, opacity: 0.55 } },
  mosaic: { position: 'random', randomCount: 3, rotation: 330, text: { size: 4, opacity: 0.35 }, image: { size: 9, opacity: 0.3 } }
}

export interface WatermarkConfig {
  enabled: boolean
  type: 'image' | 'text'
  text?: string
  image?: { id: string; url: string }
  color?: string
  position: PlacementPreset['position']
  randomCount?: number
  rotation: number
  size: number
  opacity: number
}

export function watermarkFromDraft(draft: WatermarkDraft, logo?: { id: string; url: string } | null): WatermarkConfig {
  const preset = PLACEMENT_PRESETS[draft.placement]
  const useLogo = draft.type === 'image' && !!logo
  const { size, opacity } = useLogo ? preset.image : preset.text
  return {
    enabled: draft.enabled,
    type: useLogo ? 'image' : 'text',
    ...(useLogo ? { image: logo! } : { text: draft.text.trim() || "Marca d'água", color: '#ffffff' }),
    position: preset.position,
    ...(preset.randomCount ? { randomCount: preset.randomCount } : {}),
    rotation: preset.rotation,
    size,
    opacity
  }
}

export function watermarkDraftFrom(
  saved: { enabled?: boolean; type?: string; text?: string; position?: string } | null | undefined,
  companyName: string,
  hasLogo: boolean
): WatermarkDraft {
  if (!saved) return { enabled: true, type: hasLogo ? 'image' : 'text', text: companyName, placement: 'corner' }
  const placement: WatermarkPlacement = saved.position === 'random' ? 'mosaic' : saved.position === 'middle-center' ? 'center' : 'corner'
  return {
    enabled: saved.enabled ?? true,
    type: saved.type === 'image' && hasLogo ? 'image' : 'text',
    text: saved.text || companyName,
    placement
  }
}

export function durationLabel(seconds: number): string {
  const minutes = Math.floor(seconds / 60)
  const rest = seconds % 60
  if (minutes === 0) return `${rest}s`
  return rest ? `${minutes}min ${rest}s` : `${minutes}min`
}
